import { configureStore, createSlice } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { persistReducer } from "redux-persist";
import thunk from "redux-thunk";
import rewardsReducer from "../rewards/rewardsSlice";

const defaultUserDataState = {
  personalInfo: [],
  loginData: [],
  renewPapersData: [],
  renewalPolicyReferences: {},
  plateNumber: "",
  resetEmail: "",
  customerPeriodEnd: null,
  salamMeta: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
  },
  agMeta: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
  },
  makeModelMapping: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  pinPriceSheet: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
};

let userDataSlice = createSlice({
  name: "User Data",
  initialState: defaultUserDataState,
  reducers: {
    getLoginDetails(state, action) {
      const payload = action.payload;
      state.loginData = payload;
    },

    getResetEmail(state, action) {
      const payload = action.payload;
      state.resetEmail = payload;
    },

    setCustomerPeriodEnd(state, action) {
      const payload = action.payload;
      state.customerPeriodEnd = payload;
    },

    setUserPlateNumber(state, action) {
      const payload = action.payload;
      state.plateNumber = payload;
    },

    generalUserData(state, action) {
      const payload = action.payload;
      state.personalInfo = payload;
    },
    getRenewPapersData(state, action) {
      const payload = action.payload;
      state.renewPapersData = payload;
    },
    setRenewalPolicyReference(state, action) {
      const { orderId, platform, policyNumber, plateNumber } = action.payload || {};
      const existingReference = state.renewalPolicyReferences[orderId] || {};

      if (!orderId || !platform) return;

      const normalizedPolicyNumber =
        typeof policyNumber === "string" ? policyNumber.trim() : policyNumber;
      const normalizedPlateNumber =
        typeof plateNumber === "string" ? plateNumber.trim() : plateNumber;

      if (!normalizedPolicyNumber && !normalizedPlateNumber) return;

      state.renewalPolicyReferences[orderId] = {
        platform,
        policyNumber:
          normalizedPolicyNumber || existingReference.policyNumber || "",
        plateNumber:
          normalizedPlateNumber || existingReference.plateNumber || "",
        lastUpdated: new Date().toISOString(),
      };
    },
    setSalamMetaLoading(state, action) {
      state.salamMeta.loading = action.payload;
    },
    setSalamMetaData(state, action) {
      state.salamMeta.data = action.payload;
      state.salamMeta.loading = false;
      state.salamMeta.error = null;
      state.salamMeta.lastFetched = new Date().toISOString();
    },
    setSalamMetaError(state, action) {
      state.salamMeta.error = action.payload;
      state.salamMeta.loading = false;
    },
    setAgMetaLoading(state, action) {
      state.agMeta.loading = action.payload;
    },
    setAgMetaData(state, action) {
      state.agMeta.data = action.payload;
      state.agMeta.loading = false;
      state.agMeta.error = null;
      state.agMeta.lastFetched = new Date().toISOString();
    },
    setAgMetaError(state, action) {
      state.agMeta.error = action.payload;
      state.agMeta.loading = false;
    },
    setMakeModelMappingLoading(state, action) {
      state.makeModelMapping.loading = action.payload;
    },
    setMakeModelMappingData(state, action) {
      state.makeModelMapping.data = action.payload;
      state.makeModelMapping.loading = false;
      state.makeModelMapping.error = null;
      state.makeModelMapping.lastFetched = new Date().toISOString();
    },
    setMakeModelMappingError(state, action) {
      state.makeModelMapping.error = action.payload;
      state.makeModelMapping.loading = false;
    },
    setPinPriceSheetLoading(state, action) {
      state.pinPriceSheet.loading = action.payload;
    },
    setPinPriceSheetData(state, action) {
      state.pinPriceSheet.data = action.payload;
      state.pinPriceSheet.loading = false;
      state.pinPriceSheet.error = null;
      state.pinPriceSheet.lastFetched = new Date().toISOString();
    },
    setPinPriceSheetError(state, action) {
      state.pinPriceSheet.error = action.payload;
      state.pinPriceSheet.loading = false;
    },
    logout(state) {
      state.personalInfo = [];
      state.loginData = [];
      state.renewPapersData = [];
      state.renewalPolicyReferences = {};
      state.salamMeta = defaultUserDataState.salamMeta;
      state.agMeta = defaultUserDataState.agMeta;
      state.pinPriceSheet = defaultUserDataState.pinPriceSheet;
    },
  },
});

const persistConfig = {
  key: "agentProUserDetails",
  storage,
};

const persistedReducer = persistReducer(persistConfig, userDataSlice.reducer);

const store = configureStore({
  reducer: {
    userData: persistedReducer,
    rewards: rewardsReducer,
  },
  middleware: [thunk],
});

export const userDataActions = userDataSlice.actions;
export default store;
